// client/components/evaluate/StaleBadge.tsx
//
// Surfaces when a HITL decision's context has shifted since it was recorded.
// Two distinct cases — same component, but the copy and palette differ so a
// reader can triage at a glance:
//
//   (a) Machine verdict changed         → "STALE" (amber, alarming)
//                                         The decision still applies, but the
//                                         engine now disagrees with the
//                                         machine snapshot the human reviewed.
//                                         Worth re-affirming when convenient.
//
//   (b) Engine version bumped            → "EARLIER ENGINE" (slate, informational)
//                                         Per DC-1570 Decision 9 (2026-05-13):
//                                         existing decisions remain in full
//                                         effect; the badge is a passive
//                                         provenance marker, not a re-review
//                                         prompt. Kathleen keeps agency.
//
import React from 'react';
import type { EvidencePayload } from '../../hooks/useEvaluateData';

interface StaleBadgeProps {
  decision: NonNullable<EvidencePayload['human_decision']>;
  currentEngineVersion: string;
}

export function StaleBadge({ decision, currentEngineVersion }: StaleBadgeProps) {
  if (!decision.stale) return null;
  const sameEngine = decision.engine_version_at_decision_time === currentEngineVersion;

  // Case (a) — same engine, machine verdict moved. Genuine "stale" — amber palette.
  if (sameEngine) {
    // DC-1589: dropped the U+23F5 BLACK MEDIUM RIGHT-POINTING TRIANGLE
    // ("play" glyph) — the prior `&#9205;` rendered as a tiny green play-button
    // on the amber chip and read as a UI accident, not a warning. Epic F
    // direction: let the word STALE carry the signal.
    return (
      <span
        data-testid="stale-badge"
        data-stale-cause="machine-verdict-changed"
        role="status"
        aria-label={`Stale: machine verdict changed since you decided (was ${decision.machine_verdict_at_decision_time}). Decision still in effect; reaffirm when convenient.`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 9999,
          background: 'var(--tri-soft-bg)',
          color: 'var(--tri-soft)',
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          border: '1px solid var(--tri-soft)',
        }}
        title={`Machine verdict changed since you decided (was ${decision.machine_verdict_at_decision_time}). Decision still in effect; reaffirm when convenient.`}
      >
        STALE
      </span>
    );
  }

  // Case (b) — engine version bumped. Informational only — slate palette.
  // Decision authored under v${earlier}; the v${current} engine has evolved
  // since (e.g. tier rubrics refined, new evidence emitters), but the human
  // verdict still applies with no automatic re-review prompt.
  return (
    <span
      data-testid="stale-badge"
      data-stale-cause="engine-version-bumped"
      role="status"
      aria-label={`Decision authored under engine v${decision.engine_version_at_decision_time}; current engine is v${currentEngineVersion}. Decision remains in effect.`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 9999,
        background: 'var(--tri-neutral-bg)',
        color: 'var(--tri-slate)',
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        border: '1px solid var(--tri-taupe)',
      }}
      title={`Decision authored under engine v${decision.engine_version_at_decision_time}; the v${currentEngineVersion} engine has evolved since. Your verdict remains in effect — reaffirm at your discretion.`}
    >
      v{decision.engine_version_at_decision_time}
    </span>
  );
}
